/**
 * Hook de connexion Socket.io au namespace overlays (public, sans authentification).
 * Responsabilité unique : connecter, enregistrer les listeners overlay.show / leaderboard.update / battle.*, cleanup.
 */

import { useState, useEffect } from 'react';

const isDev =
  typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test';

/**
 * Hook : connexion au namespace /overlays, état connected, enregistrement des listeners.
 * @param {Object} socketService - Service Socket (connectToOverlays)
 * @param {{ onOverlayShow: function, onLeaderboardUpdate: function, onBattleStart?: function, onBattleState?: function, onBattleEnd?: function }} callbacks - Handlers socket
 * @param {{ timeoutRef?: { current: ReturnType<typeof setTimeout> | null }, leaderboardHideTimerRef?: { current: ReturnType<typeof setTimeout> | null } }} refsToClean - Refs à nettoyer au démontage (optionnel)
 * @returns {{ connected: boolean }}
 */
export function useOverlaySocket(socketService, callbacks, refsToClean = {}) {
  const { onOverlayShow, onLeaderboardUpdate, onBattleStart, onBattleState, onBattleEnd } = callbacks;
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!socketService?.connectToOverlays) return;

    if (isDev) console.log('🔌 Tentative de connexion au namespace /overlays...');
    const socket = socketService.connectToOverlays();

    const handleConnect = () => {
      if (isDev) {
        console.log(
          '✅ OverlayPlayer connecté au serveur Socket.io (namespace /overlays)'
        );
      }
      setConnected(true);
    };

    const handleDisconnect = () => {
      if (isDev) console.log('❌ OverlayPlayer déconnecté du serveur Socket.io');
      setConnected(false);
    };

    const handleConnectError = (error) => {
      console.error('❌ Erreur de connexion Socket.io:', error);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);

    socket.off('overlay.show');
    const overlayShowListener = (data) => {
      if (isDev) {
        console.log('🎬 Événement overlay.show reçu dans OverlayPlayer:', data);
      }
      onOverlayShow(data);
    };
    socket.on('overlay.show', overlayShowListener);

    const leaderboardUpdateListener = (data) => {
      onLeaderboardUpdate(data);
    };
    socket.on('leaderboard.update', leaderboardUpdateListener);

    const battleStartListener = (data) => onBattleStart && onBattleStart(data);
    const battleStateListener = (data) => onBattleState && onBattleState(data);
    const battleEndListener = (data) => onBattleEnd && onBattleEnd(data);
    socket.on('battle.start', battleStartListener);
    socket.on('battle.state', battleStateListener);
    socket.on('battle.end', battleEndListener);

    if (isDev) console.log('👂 Listener overlay.show enregistré');

    // Quand la page redevient visible (ex: retour sur la scène OBS), forcer la reconnexion si déconnecté
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !socket.connected) {
        if (isDev) console.log('🔄 Page visible et socket déconnecté → reconnexion...');
        socket.connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    let reconnectTimeoutId = null;
    if (document.visibilityState === 'visible' && !socket.connected) {
      reconnectTimeoutId = setTimeout(() => {
        if (!socket.connected) socket.connect();
      }, 500);
    }

    return () => {
      if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (isDev) console.log('🧹 Nettoyage: déconnexion du listener overlay.show');
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('overlay.show', overlayShowListener);
      socket.off('leaderboard.update', leaderboardUpdateListener);
      socket.off('battle.start', battleStartListener);
      socket.off('battle.state', battleStateListener);
      socket.off('battle.end', battleEndListener);
      if (refsToClean.timeoutRef?.current) {
        clearTimeout(refsToClean.timeoutRef.current);
        refsToClean.timeoutRef.current = null;
      }
      if (refsToClean.leaderboardHideTimerRef?.current) {
        clearTimeout(refsToClean.leaderboardHideTimerRef.current);
        refsToClean.leaderboardHideTimerRef.current = null;
      }
    };
  }, [
    onOverlayShow,
    onLeaderboardUpdate,
    onBattleStart,
    onBattleState,
    onBattleEnd,
    socketService,
    refsToClean.leaderboardHideTimerRef,
    refsToClean.timeoutRef,
  ]);

  return { connected };
}
